import { useMemo, useState } from "react";
import { useAuth } from "../../../app/AuthContext";
import {
  Plus,
  Search,
  MoreVertical,
  Users,
  Trash2,
  Edit,
  GraduationCap,
  Calendar,
  X,
} from "lucide-react";
import StudentsManager from "./StudentsManager";
import { useSchoolProfile } from "../../../app/SchoolProfileContext";
import { type ClassData } from "../../../lib/api";
import { useClasses, useCreateClass, useUpdateClass, useDeleteClass } from "../../../hooks/useClasses";
import { useEnrollments } from "../../../hooks/useEnrollments";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../../lib/queryClient";

const COLORS = [
  "#1967d2", // Blue
  "#188038", // Green
  "#f9ab00", // Yellow
  "#d93025", // Red
  "#9334e6", // Purple
  "#e37400", // Orange
  "#0097a7", // Cyan
  "#f06292", // Pink
];

interface ClassesManagerProps {
  onClassClick?: (className: string) => void;
  onRefresh?: () => void;
  classes?: any[]; // Added to match Dashboard usage
}

export default function ClassesManager({
  onClassClick,
  onRefresh,
  classes: initialClasses, // Renamed to avoid confusion with internal state
}: ClassesManagerProps) {
  const { token } = useAuth();
  const { activeProfile } = useSchoolProfile();
  const queryClient = useQueryClient();

  // Use React Query hooks - data is cached and shared across components!
  const { data: classes = [], isLoading: classesLoading } = useClasses(token, activeProfile?.id);
  const { data: enrollments = [], isLoading: enrollmentsLoading } = useEnrollments(token, activeProfile?.id);
  const createClassMutation = useCreateClass(token, activeProfile?.id);
  const updateClassMutation = useUpdateClass(token, activeProfile?.id);
  const deleteClassMutation = useDeleteClass(token, activeProfile?.id);

  const loading = classesLoading || enrollmentsLoading;

  // Calculate enrollment counts from cached enrollments
  const enrollmentCountByClassId = useMemo(() => {
    const counts: Record<string, number> = {};
    enrollments.forEach((e: any) => {
      counts[e.class_id] = (counts[e.class_id] || 0) + 1;
    });
    return counts;
  }, [enrollments]);

  // UI state
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStudentsModalOpen, setIsStudentsModalOpen] = useState(false);
  const [selectedClassForStudents, setSelectedClassForStudents] =
    useState<ClassData | null>(null);
  const [editingclass, setEditingClass] = useState<ClassData | null>(null);

  const [formData, setFormData] = useState<{
    name: string;
    schedules: Array<{ dayOfWeek: string; startTime: string; endTime: string }>;
    color: string;
  }>({
    name: "",
    schedules: [
      { dayOfWeek: "Segunda-feira", startTime: "08:00", endTime: "10:00" },
    ],
    color: COLORS[0] || "#1967d2",
  });

  // Invalidate cache after mutations
  const invalidateCache = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.classes.list(activeProfile?.id) });
    queryClient.invalidateQueries({ queryKey: queryKeys.enrollments.list(activeProfile?.id) });
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
  };

  const filteredClasses = useMemo(() => {
    return classes.filter((c) => {
      const subject = c.subject || "";
      return (
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        subject.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  }, [classes, searchTerm]);

  const handleOpenModal = (cls?: ClassData) => {
    if (cls) {
      setEditingClass(cls);

      let parsedSchedules: Array<{
        dayOfWeek: string;
        startTime: string;
        endTime: string;
      }> = [];

      if (cls.schedule) {
        const slots = cls.schedule.split(" | ");
        slots.forEach((slot) => {
          if (slot.includes(" - ") && slot.includes(" às ")) {
            const parts = slot.split(" - ");
            const day = parts[0] || "Segunda-feira";
            if (parts[1]) {
              const times = parts[1].split(" às ");
              parsedSchedules.push({
                dayOfWeek: day,
                startTime: times[0] || "08:00",
                endTime: times[1] || "10:00",
              });
            }
          }
        });
      }

      if (parsedSchedules.length === 0) {
        parsedSchedules = [
          { dayOfWeek: "Segunda-feira", startTime: "08:00", endTime: "10:00" },
        ];
      }

      setFormData({
        name: cls.name,
        schedules: parsedSchedules,
        color: cls.color || COLORS[0] || "#1967d2",
      });
    } else {
      setEditingClass(null);
      setFormData({
        name: "",
        schedules: [
          { dayOfWeek: "Segunda-feira", startTime: "08:00", endTime: "10:00" },
        ],
        color: COLORS[0] || "#1967d2",
      });
    }
    setIsModalOpen(true);
  };

  const addSchedule = () => {
    setFormData((prev) => ({
      ...prev,
      schedules: [
        ...prev.schedules,
        { dayOfWeek: "Segunda-feira", startTime: "08:00", endTime: "10:00" },
      ],
    }));
  };

  const removeSchedule = (index: number) => {
    if (formData.schedules.length <= 1) return;
    setFormData((prev) => ({
      ...prev,
      schedules: prev.schedules.filter((_, i) => i !== index),
    }));
  };

  const handleSave = () => {
    if (!token) return;
    
    if (!activeProfile?.id) {
      alert("Erro: Nenhum perfil de escola selecionado.");
      return;
    }

    const scheduleString = formData.schedules
      .map((s) => `${s.dayOfWeek} - ${s.startTime} às ${s.endTime}`)
      .join(" | ");

    // Close modal immediately for "real-time" feel
    setIsModalOpen(false);

    if (editingclass) {
      // Prune read-only fields
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, created_at, updated_at, studentsCount, ...writableData } =
        editingclass as any;

      const payload = {
        ...writableData,
        name: formData.name.trim(),
        schedule: scheduleString,
        color: formData.color,
        subject: writableData.subject || "",
      };

      updateClassMutation.mutate(
        { id: editingclass.id, data: payload },
        {
          onSuccess: () => {
            if (onRefresh) onRefresh();
          },
          onError: (error) => {
            console.error(error);
            // Re-open modal or show toast on error
            alert("Erro ao atualizar turma. Suas alterações foram revertidas.");
            setEditingClass(editingclass);
            setIsModalOpen(true);
          }
        }
      );
    } else {
      const gradeYear = inferGradeYear(formData.name);
      createClassMutation.mutate(
        {
          name: formData.name.trim(),
          schedule: scheduleString,
          color: formData.color,
          grade_year: gradeYear,
          school_level: inferSchoolLevel(gradeYear),
          school_year: new Date().getFullYear(),
          school_profile_id: activeProfile?.id,
        },
        {
          onSuccess: () => {
            if (onRefresh) onRefresh();
          },
          onError: (error) => {
            console.error(error);
            alert("Erro ao criar turma.");
            setIsModalOpen(true);
          }
        }
      );
    }
  };

  const handleDelete = (id: string) => {
    if (!window.confirm("Tem certeza que deseja excluir esta turma?")) return;
    deleteClassMutation.mutate(id, {
      onSuccess: () => {
        if (onRefresh) onRefresh();
      },
      onError: (error) => {
        console.error(error);
        alert("Erro ao excluir turma.");
      }
    });
  };

  return (
    <div
      className="bg-(--card-bg) rounded-xl shadow-sm border"
      style={{ borderColor: "var(--border-color)" }}
    >
      <div
        className="p-6 border-b"
        style={{ borderColor: "var(--border-color)" }}
      >
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="relative w-full md:w-96">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
              style={{ color: "var(--text-tertiary)" }}
            />
            <input
              type="text"
              placeholder="Buscar por nome ou matéria..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition bg-(--input-bg) text-(--text-primary)"
              style={{ borderColor: "var(--border-color)" }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 px-6 py-2 rounded-lg text-white font-medium hover:shadow-md transition w-full md:w-auto justify-center"
            style={{ backgroundColor: "var(--primary-color)" }}
          >
            <Plus className="w-5 h-5" />
            Nova Turma
          </button>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClasses.map((cls) => (
            <div
              key={cls.id}
              onClick={() => onClassClick && onClassClick(cls.name)}
              className="bg-(--card-bg) rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition group cursor-pointer"
              style={{ borderColor: "var(--border-color)" }}
            >
              <div
                className="h-2"
                style={{ backgroundColor: cls.color || "var(--primary-color)" }}
              ></div>
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3
                      className="text-xl font-bold mb-1"
                      style={{ color: "var(--text-primary)" }}
                    >
                      {cls.name}
                    </h3>
                    <p
                      className="text-sm font-medium"
                      style={{ color: cls.color || "var(--primary-color)" }}
                    >
                      {cls.subject || ""}
                    </p>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal(cls);
                      }}
                      className="p-2 hover:bg-(--bg-accent) rounded-full transition"
                    >
                      <Edit className="w-4 h-4 text-(--text-tertiary)" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(cls.id);
                      }}
                      className="p-2 hover:bg-red-50 rounded-full transition"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm text-(--text-tertiary)">
                    <Users className="w-4 h-4" />
                    <span>{enrollmentCountByClassId[cls.id] || 0} Alunos</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm text-(--text-tertiary)">
                    <Calendar className="w-4 h-4 mt-0.5 shrink-0" />
                    <div className="flex flex-col gap-1">
                      {cls.schedule ? (
                        cls.schedule.split(" | ").map((slot, i) => (
                          <span key={i} className="leading-tight">
                            {slot}
                          </span>
                        ))
                      ) : (
                        <span>Sem horário</span>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className="mt-6 pt-4 border-t flex justify-between items-center"
                  style={{ borderColor: "var(--border-color)" }}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedClassForStudents(cls);
                      setIsStudentsModalOpen(true);
                    }}
                    className="text-sm font-medium hover:bg-(--bg-accent) px-3 py-1.5 rounded-lg transition flex items-center gap-2"
                    style={{ color: "var(--text-primary)" }}
                  >
                    <Users className="w-4 h-4" /> Alunos
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onClassClick && onClassClick(cls.name);
                    }}
                    className="text-sm font-medium hover:underline flex items-center gap-1"
                    style={{ color: cls.color || "var(--primary-color)" }}
                  >
                    Ver Atividades <MoreVertical className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredClasses.length === 0 && (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-(--bg-accent) rounded-full flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="w-10 h-10 text-(--text-tertiary)" />
            </div>
            <h3 className="text-xl font-medium text-(--text-primary) mb-2">
              Nenhuma turma encontrada
            </h3>
            <p className="text-(--text-tertiary)">
              Tente buscar por outro termo ou crie uma nova turma.
            </p>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-(--card-bg) rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            <div
              className="px-6 py-4 border-b flex justify-between items-center"
              style={{ borderColor: "var(--border-color)" }}
            >
              <h3 className="text-lg font-semibold text-(--text-primary)">
                {editingclass ? "Editar Turma" : "Nova Turma"}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-(--text-tertiary) hover:text-(--text-secondary)"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-(--text-secondary) mb-1">
                  Nome da Turma
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 outline-none bg-(--input-bg) text-(--text-primary)"
                  style={
                    {
                      borderColor: "var(--border-color)",
                      "--tw-ring-color": formData.color,
                    } as any
                  }
                  placeholder="Ex: 9º Ano A"
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-(--text-secondary)">
                    Horários da Turma
                  </label>
                  <button
                    onClick={addSchedule}
                    className="flex items-center gap-1 text-xs font-medium text-(--primary-color) hover:bg-(--bg-accent) px-2 py-1 rounded-md transition"
                  >
                    <Plus className="w-3 h-3" /> Adicionar Dia
                  </button>
                </div>

                <div className="space-y-4 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                  {formData.schedules.map((schedule, index) => (
                    <div
                      key={index}
                      className="p-3 bg-(--bg-secondary) rounded-xl border border-(--border-color) relative group"
                    >
                      {formData.schedules.length > 1 && (
                        <button
                          onClick={() => removeSchedule(index)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition shadow-sm z-10"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}

                      <div className="space-y-3">
                        <div>
                          <label className="block text-[10px] font-bold uppercase tracking-wider text-(--text-tertiary) mb-1">
                            Dia da Semana
                          </label>
                          <select
                            value={schedule.dayOfWeek}
                            onChange={(e) => {
                              const newSchedules = [...formData.schedules];
                              if (newSchedules[index]) {
                                newSchedules[index].dayOfWeek = e.target.value;
                                setFormData({
                                  ...formData,
                                  schedules: newSchedules,
                                });
                              }
                            }}
                            className="w-full px-3 py-1.5 text-sm border rounded-lg outline-none bg-(--input-bg) border-(--border-color) text-(--text-primary)"
                          >
                            {[
                              "Segunda-feira",
                              "Terça-feira",
                              "Quarta-feira",
                              "Quinta-feira",
                              "Sexta-feira",
                              "Sábado",
                              "Domingo",
                            ].map((day) => (
                              <option key={day} value={day}>
                                {day}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[10px] font-bold uppercase tracking-wider text-(--text-tertiary) mb-1">
                              Início
                            </label>
                            <input
                              type="time"
                              value={schedule.startTime}
                              onChange={(e) => {
                                const newSchedules = [...formData.schedules];
                                if (newSchedules[index]) {
                                  newSchedules[index].startTime =
                                    e.target.value;
                                  setFormData({
                                    ...formData,
                                    schedules: newSchedules,
                                  });
                                }
                              }}
                              className="w-full px-3 py-1.5 text-sm border rounded-lg outline-none bg-(--input-bg) border-(--border-color) text-(--text-primary)"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold uppercase tracking-wider text-(--text-tertiary) mb-1">
                              Término
                            </label>
                            <input
                              type="time"
                              value={schedule.endTime}
                              onChange={(e) => {
                                const newSchedules = [...formData.schedules];
                                if (newSchedules[index]) {
                                  newSchedules[index].endTime = e.target.value;
                                  setFormData({
                                    ...formData,
                                    schedules: newSchedules,
                                  });
                                }
                              }}
                              className="w-full px-3 py-1.5 text-sm border rounded-lg outline-none bg-(--input-bg) border-(--border-color) text-(--text-primary)"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-(--text-secondary) mb-2">
                  Cor do Card
                </label>
                <div className="flex flex-wrap gap-2">
                  {COLORS.map((color) => (
                    <button
                      key={color}
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${formData.color === color
                        ? "ring-2 ring-offset-2 ring-gray-400"
                        : ""
                        }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-(--bg-secondary) flex justify-end gap-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-(--text-secondary) font-medium hover:bg-(--bg-accent) rounded-lg transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={!formData.name.trim()}
                className="px-4 py-2 text-white font-medium rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: formData.color }}
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
      {isStudentsModalOpen && selectedClassForStudents && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-(--card-bg) rounded-xl shadow-xl w-full max-w-2xl h-[80vh] overflow-hidden animate-in fade-in zoom-in duration-200">
            <StudentsManager
              className={selectedClassForStudents.name}
              classId={selectedClassForStudents.id}
              onClose={() => {
                setIsStudentsModalOpen(false);
                setSelectedClassForStudents(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Helper functions for class metadata
function inferGradeYear(name: string): number {
  const match = name.match(/(\d+)/);
  return match?.[1] ? parseInt(match[1]) : 1;
}

function inferSchoolLevel(grade: number): string {
  if (grade <= 9) return "fundamental";
  return "medio";
}
